import React, { useState, useEffect } from 'react';
import Parse from 'parse';
import { BethelAppClasses } from '../../api/config';

const BulletinManager = () => {
    const [bulletins, setBulletins] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    useEffect(() => {
        fetchBulletins();
    }, []);

    const fetchBulletins = async () => {
        try {
            setLoading(true);
            setError('');
            
            // Save current Parse configuration
            const originalServerURL = Parse.serverURL;
            const originalAppId = Parse.applicationId;
            
            // Switch to Bethel app server
            Parse.serverURL = 'https://app.bethelchristiancenter.com/parse';
            Parse._initialize('bethel', null, null);
            
            const bulletinsData = await Parse.Cloud.run('getBulletins');
            setBulletins(bulletinsData || []);
            
            // Restore original Parse configuration
            Parse.serverURL = originalServerURL;
            Parse._initialize(originalAppId, null, null);
        } catch (err) {
            console.error('Error fetching bulletins:', err);
            setError('Failed to load bulletins. Please try again.');
            
            // Restore original Parse configuration on error
            const originalServerURL = 'https://forms.bethelchristiancenter.com/api/parse';
            const originalAppId = 'myAppId';
            Parse.serverURL = originalServerURL;
            Parse._initialize(originalAppId, null, null);
        } finally {
            setLoading(false);
        }
    };

    const handleFileUpload = async (e) => {
        e.preventDefault();
        setUploading(true);
        setError('');
        setSuccessMessage('');

        const title = e.target.title.value;
        const date = e.target.date.value;
        const file = e.target.file.files[0];

        if (!file) {
            setError('Please select a file to upload.');
            setUploading(false);
            return;
        }

        try {
            // Save current Parse configuration
            const originalServerURL = Parse.serverURL;
            const originalAppId = Parse.applicationId;
            
            // Switch to Bethel app server
            Parse.serverURL = 'https://app.bethelchristiancenter.com/parse';
            Parse._initialize('bethel', null, null);

            // Create Parse File
            const parseFile = new Parse.File(file.name, file);
            await parseFile.save();

            // Create Bulletin object
            const Bulletin = Parse.Object.extend(BethelAppClasses.Bulletin);
            const bulletin = new Bulletin();
            
            bulletin.set('bulletinTitle', title);
            bulletin.set('bulletinFile', parseFile);
            bulletin.set('bulletinDate', new Date(date));
            
            await bulletin.save();
            
            // Restore original Parse configuration
            Parse.serverURL = originalServerURL;
            Parse._initialize(originalAppId, null, null);
            
            setSuccessMessage('Bulletin uploaded successfully!');
            e.target.reset();
            fetchBulletins(); // Refresh the list
        } catch (err) {
            console.error('Error uploading bulletin:', err);
            setError('Failed to upload bulletin. Please try again.');
            
            // Restore original Parse configuration on error
            const originalServerURL = 'https://forms.bethelchristiancenter.com/api/parse';
            const originalAppId = 'myAppId';
            Parse.serverURL = originalServerURL;
            Parse._initialize(originalAppId, null, null);
        } finally {
            setUploading(false);
        }
    };

    const handleDelete = async (bulletinId) => {
        if (!window.confirm('Are you sure you want to delete this bulletin?')) {
            return;
        }

        try {
            // Save current Parse configuration
            const originalServerURL = Parse.serverURL;
            const originalAppId = Parse.applicationId;
            
            // Switch to Bethel app server
            Parse.serverURL = 'https://app.bethelchristiancenter.com/parse';
            Parse._initialize('bethel', null, null);

            const Bulletin = Parse.Object.extend(BethelAppClasses.Bulletin);
            const query = new Parse.Query(Bulletin);
            const bulletin = await query.get(bulletinId);
            
            await bulletin.destroy();
            
            // Restore original Parse configuration
            Parse.serverURL = originalServerURL;
            Parse._initialize(originalAppId, null, null);
            
            setSuccessMessage('Bulletin deleted successfully!');
            fetchBulletins(); // Refresh the list
        } catch (err) {
            console.error('Error deleting bulletin:', err);
            setError('Failed to delete bulletin. Please try again.');
            
            // Restore original Parse configuration on error
            const originalServerURL = 'https://forms.bethelchristiancenter.com/api/parse';
            const originalAppId = 'myAppId';
            Parse.serverURL = originalServerURL;
            Parse._initialize(originalAppId, null, null);
        }
    };

    const formatDate = (date) => {
        return new Date(date).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div>
            <h2 className="text-2xl font-bold mb-6">Weekly Bulletin Management</h2>

            {/* Upload Form */}
            <div className="bg-gray-50 rounded-lg p-6 mb-6">
                <h3 className="text-lg font-semibold mb-4">Upload New Bulletin</h3>
                
                {error && (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                        {error}
                    </div>
                )}
                
                {successMessage && (
                    <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
                        {successMessage}
                    </div>
                )}

                <form onSubmit={handleFileUpload} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Bulletin Title
                        </label>
                        <input
                            type="text"
                            name="title"
                            required
                            className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500"
                            placeholder="e.g., Weekly Bulletin - January 7, 2024"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Bulletin Date
                        </label>
                        <input
                            type="date"
                            name="date"
                            required
                            className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500"
                            defaultValue={new Date().toISOString().split('T')[0]}
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Bulletin File (PDF)
                        </label>
                        <input
                            type="file"
                            name="file"
                            accept=".pdf"
                            required
                            className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                            Accepted formats: PDF
                        </p>
                    </div>

                    <button
                        type="submit"
                        disabled={uploading}
                        className="bg-blue-600 text-white py-2 px-6 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                    >
                        {uploading ? 'Uploading...' : 'Upload Bulletin'}
                    </button>
                </form>
            </div>

            {/* Bulletins List */}
            <div>
                <h3 className="text-lg font-semibold mb-4">Existing Bulletins</h3>
                
                {bulletins.length === 0 ? (
                    <p className="text-gray-500 text-center py-8">
                        No bulletins uploaded yet. Upload your first bulletin above.
                    </p>
                ) : (
                    <div className="space-y-3">
                        {bulletins.map((bulletin) => (
                            <div
                                key={bulletin.id}
                                className="bg-white border rounded-lg p-4 flex items-center justify-between hover:shadow-md transition-shadow"
                            >
                                <div className="flex-1">
                                    <h4 className="font-medium text-gray-900">
                                        {bulletin.get('bulletinTitle')}
                                    </h4>
                                    <p className="text-sm text-gray-500">
                                        {formatDate(bulletin.get('bulletinDate'))}
                                    </p>
                                </div>
                                
                                <div className="flex items-center space-x-3">
                                    <a
                                        href={bulletin.get('bulletinFile').url()}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-blue-600 hover:text-blue-800 font-medium"
                                    >
                                        Download
                                    </a>
                                    <button
                                        onClick={() => handleDelete(bulletin.id)}
                                        className="text-red-600 hover:text-red-800 font-medium"
                                    >
                                        Delete
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default BulletinManager;